import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { LoadingSpinner } from '../generic/Loading';
import { getCourseOutlineChildren } from './data/api';
import SubsectionCard from './subsection-card/SubsectionCard';
import SubsectionChildren from './SubsectionChildren';
import { possibleSubsectionMoves } from '../generic/drag-helper/utils';

/**
 * SectionChildren
 * - loads subsections via course_index children endpoint (as before)
 * - renders SubsectionCard for each subsection and puts SubsectionChildren inside it (which will load units)
 */
const SectionChildren = ({
  courseId,
  section,
  sectionIndex,
  sections,
  isSelfPaced,
  isCustomRelativeDatesActive,
  savingStatus,
  openPublishModal,
  openConfigureModal,
  openDeleteModal,
  handleEditSubmit,
  handleDuplicateSubsectionSubmit,
  handleDuplicateUnitSubmit,
  handleNewUnitSubmit,
  handlePasteClipboardClick,
  updateSubsectionOrderByIndex,
  updateUnitOrderByIndex,
  discussionsSettings,
  demoUnitKeys,
  // forwarder for unit title links
  getTitleLink,
}) => {
  const [loaded, setLoaded] = useState(Boolean(section?.childInfo?.children && section.childInfo.children.length));
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [childrenList, setChildrenList] = useState(section?.childInfo?.children || []);
  const [pageMeta, setPageMeta] = useState(null);
  const [error, setError] = useState(null);

  const computePossibleSubsectionMoves = useMemo(() => (
    (index, step) => possibleSubsectionMoves([...sections], sectionIndex, section, childrenList, index, step)
  ), [sections, sectionIndex, section, childrenList]);

  const loadSubsections = async () => {
    if (isLoading || !courseId || !section?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const resp = await getCourseOutlineChildren(courseId, {
        parent: section.id,
        limit: 20,
        offset: 0,
        format: 'full',
        prefetchDepth: 0,
      });

      const fetched = resp.sections || resp.course_structure?.child_info?.children || resp.children || [];
      const respPageMeta = resp.pageMeta || resp.page_meta || null;

      setChildrenList(fetched);
      setPageMeta(respPageMeta);
      setLoaded(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load subsections for', section.id, err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreSubsections = async () => {
    if (isLoadingMore || !pageMeta) return;
    setIsLoadingMore(true);
    setError(null);
    try {
      const limit = pageMeta.limit ?? pageMeta.page_size ?? 20;
      const offset = pageMeta.offset ?? (childrenList.length || 0);
      const nextOffset = offset + limit;

      const resp = await getCourseOutlineChildren(courseId, {
        parent: section.id,
        limit,
        offset: nextOffset,
        format: 'full',
        prefetchDepth: 0,
      });

      const more = resp.sections || resp.course_structure?.child_info?.children || resp.children || [];
      const respPageMeta = resp.pageMeta || resp.page_meta || null;

      setChildrenList((prev) => {
        const existing = new Set(prev.map((c) => c.id));
        const toAppend = more.filter((m) => !existing.has(m.id));
        return prev.concat(toAppend);
      });

      if (respPageMeta) {
        setPageMeta(respPageMeta);
      } else {
        setPageMeta((prev) => ({ ...(prev || {}), offset: (prev?.offset ?? 0) + limit }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load more subsections for', section.id, err);
      setError(err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (!loaded) {
    return (
      <div className="py-2">
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={loadSubsections}
          disabled={isLoading}
        >
          {isLoading ? <LoadingSpinner /> : 'Load subsections'}
        </button>
        {error && <div className="text-danger small">Failed to load subsections</div>}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-3">
        <LoadingSpinner />
      </div>
    );
  }

  if (!childrenList || childrenList.length === 0) {
    return (
      <div className="py-2 text-muted small">
        No subsections
      </div>
    );
  }

  const hasMore = (() => {
    if (!pageMeta) return false;
    const total = pageMeta.total_count ?? pageMeta.totalCount ?? pageMeta.count ?? null;
    const limit = pageMeta.limit ?? pageMeta.page_size ?? null;
    if (total === null) {
      return childrenList.length === (limit || 0);
    }
    return childrenList.length < total;
  })();

  return (
    <>
      <SortableContext
        id={section.id}
        items={childrenList}
        strategy={verticalListSortingStrategy}
      >
        {childrenList.map((subsection, subsectionIndex) => (
          <SubsectionCard
            key={subsection.id}
            section={section}
            subsection={subsection}
            index={subsectionIndex}
            getPossibleMoves={computePossibleSubsectionMoves}
            isSelfPaced={isSelfPaced}
            isCustomRelativeDatesActive={isCustomRelativeDatesActive}
            savingStatus={savingStatus}
            onOpenPublishModal={openPublishModal}
            onOpenDeleteModal={openDeleteModal}
            onEditSubmit={handleEditSubmit}
            onDuplicateSubmit={handleDuplicateSubsectionSubmit}
            onOpenConfigureModal={openConfigureModal}
            onNewUnitSubmit={handleNewUnitSubmit}
            onOrderChange={updateSubsectionOrderByIndex}
            onPasteClick={handlePasteClipboardClick}
          >
            {/* pass getTitleLink down to SubsectionChildren */}
            <SubsectionChildren
              courseId={courseId}
              section={section}
              subsection={subsection}
              sectionIndex={sectionIndex}
              subsectionIndex={subsectionIndex}
              sections={sections}
              isSelfPaced={isSelfPaced}
              isCustomRelativeDatesActive={isCustomRelativeDatesActive}
              savingStatus={savingStatus}
              openPublishModal={openPublishModal}
              openConfigureModal={openConfigureModal}
              openDeleteModal={openDeleteModal}
              handleEditSubmit={handleEditSubmit}
              handleDuplicateUnitSubmit={handleDuplicateUnitSubmit}
              handleNewUnitSubmit={handleNewUnitSubmit}
              handlePasteClipboardClick={handlePasteClipboardClick}
              updateUnitOrderByIndex={updateUnitOrderByIndex}
              discussionsSettings={discussionsSettings}
              demoUnitKeys={demoUnitKeys}
              getTitleLink={getTitleLink}
            />
          </SubsectionCard>
        ))}
      </SortableContext>

      {hasMore && (
        <div className="py-2">
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={loadMoreSubsections}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? <LoadingSpinner /> : 'Load more subsections'}
          </button>
          {error && <div className="text-danger small">Failed to load more subsections</div>}
        </div>
      )}
    </>
  );
};

SectionChildren.propTypes = {
  courseId: PropTypes.string.isRequired,
  section: PropTypes.object.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  sections: PropTypes.array.isRequired,
  isSelfPaced: PropTypes.bool,
  isCustomRelativeDatesActive: PropTypes.bool,
  savingStatus: PropTypes.string,
  openPublishModal: PropTypes.func,
  openConfigureModal: PropTypes.func,
  openDeleteModal: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleDuplicateSubsectionSubmit: PropTypes.func,
  handleDuplicateUnitSubmit: PropTypes.func,
  handleNewUnitSubmit: PropTypes.func,
  handlePasteClipboardClick: PropTypes.func,
  updateSubsectionOrderByIndex: PropTypes.func,
  updateUnitOrderByIndex: PropTypes.func,
  discussionsSettings: PropTypes.object,
  demoUnitKeys: PropTypes.array,
  getTitleLink: PropTypes.func,
};

SectionChildren.defaultProps = {
  isSelfPaced: false,
  isCustomRelativeDatesActive: false,
  savingStatus: '',
  openPublishModal: () => {},
  openConfigureModal: () => {},
  openDeleteModal: () => {},
  handleEditSubmit: () => {},
  handleDuplicateSubsectionSubmit: () => {},
  handleDuplicateUnitSubmit: () => {},
  handleNewUnitSubmit: () => {},
  handlePasteClipboardClick: () => {},
  updateSubsectionOrderByIndex: () => {},
  updateUnitOrderByIndex: () => {},
  discussionsSettings: {},
  demoUnitKeys: [],
  getTitleLink: () => {},
};

export default SectionChildren;